/**
 * COMPTABILITÉ — LE VOCABULAIRE COMMUN DES ÉCRANS DE LECTURE.
 *
 * Cinq surfaces partagent exactement les mêmes réflexes, et les dupliquer
 * cinq fois aurait garanti qu'elles finissent par se contredire :
 *
 *   · une ressource distante a QUATRE issues distinctes — pas encore su,
 *     lue, interdite, absente — et les confondre est précisément ce qui
 *     produit un « 0,00 € » alors que rien n'a été lu ;
 *   · un montant absent s'écrit « — », jamais « 0,00 € » (le socle
 *     `money.ts` s'en charge, encore faut-il lui passer `null` et non `0`) ;
 *   · une colonne sans donnée sur la période NE S'AFFICHE PAS ;
 *   · une nuit VALIDÉE SANS CLÔTURE DÉTAILLÉE compte dans le retenu, mais
 *     NOMMÉE à part : son propre type (`NuitValideeCA`), sa propre ligne
 *     (`LIBELLE_VALIDE`), jamais une place parmi les clôtures — et l'écran
 *     laisse refaire l'addition Calculé + Validé + Ajustements = Retenu ;
 *   · un motif de correction vaut douze caractères utiles, ici comme en base.
 *
 * ═══ POURQUOI DES LECTEURS TOLÉRANTS ═══
 *
 * Les routes `/api/v1/finance/*` sont écrites en parallèle contre le §8 du
 * contrat gelé. Ces écrans codent donc contre le CONTRAT, pas contre une
 * implémentation observée : chaque charge utile est validée à l'exécution, et
 * une réponse qui ne correspond pas est traitée comme une ABSENCE honnête —
 * jamais comme un zéro. C'est aussi ce qui rend l'écran lisible le jour où une
 * route existe mais renvoie un champ de plus ou de moins.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, BadgeCheck, History, Lock, Radio } from 'lucide-react'
import { apiGet } from '../admin/primitives.js'
import { formatEcart, formatMoney, type Cents } from './money.js'
import { REPRIS_COURT, phraseReprise, repriseDe, type RepriseHistorique } from './reprise-historique.js'

/* ══════════════════════════ lecture d'une ressource ══════════════════════ */

/**
 * L'issue d'une lecture. « Pas encore su », « pas votre droit », « pas encore
 * servi » et « rien sur la période » sont quatre états DIFFÉRENTS : le seul
 * qui autorise à afficher des chiffres est `ok`.
 */
export type Etat<T> =
  | { phase: 'inactive' }
  | { phase: 'chargement' }
  | { phase: 'ok'; data: T }
  | { phase: 'refus' }
  | { phase: 'absente'; raison: string }
  | { phase: 'erreur'; raison: string }

export function estObjet(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** Le code d'erreur d'une réponse d'API, quand c'en est une. */
function codeErreur(v: unknown): string | null {
  if (!estObjet(v)) return null
  const e = v['error']
  return typeof e === 'string' ? e : null
}

/**
 * Lit une route et RANGE la réponse dans l'un des états ci-dessus.
 *
 * `apiGet` résout sur les 404 (le corps d'erreur est un JSON valide) : sans ce
 * tri, un `{ error: 'NOT_FOUND' }` traverserait le normaliseur, en sortirait
 * vide, et l'écran afficherait une période « à zéro » qui n'a jamais été lue.
 */
export async function lireRessource<T>(
  chemin: string, valider: (v: unknown) => T | null,
): Promise<Etat<T>> {
  let brut: unknown
  try {
    brut = await apiGet<unknown>(chemin)
  } catch {
    return { phase: 'erreur', raison: 'Le serveur n’a pas répondu, ou sa réponse était illisible.' }
  }
  const err = codeErreur(brut)
  if (err === 'FORBIDDEN' || err === 'STEPUP_REQUIRED') return { phase: 'refus' }
  if (err === 'NOT_FOUND') {
    return { phase: 'absente', raison: 'Cette route n’est pas encore servie par l’API.' }
  }
  if (err !== null) {
    const m = estObjet(brut) ? brut['message'] : null
    return { phase: 'erreur', raison: typeof m === 'string' ? m : `Le serveur a refusé la lecture (${err}).` }
  }
  const data = valider(brut)
  if (data === null) {
    return { phase: 'absente', raison: 'La réponse reçue ne correspond pas au contrat attendu : rien n’a été affiché plutôt qu’un total douteux.' }
  }
  return { phase: 'ok', data }
}

/**
 * La même chose, montée en état de composant. `chemin: null` = lecture non
 * pertinente ici (période incomplète, droit absent) : on ne lance rien et on
 * n'affiche rien — surtout pas un chargement perpétuel.
 */
export function useRessource<T>(
  chemin: string | null, valider: (v: unknown) => T | null,
): { etat: Etat<T>; recharger: () => void } {
  const [etat, setEtat] = useState<Etat<T>>({ phase: chemin === null ? 'inactive' : 'chargement' })
  const [tour, setTour] = useState(0)
  // Le validateur est recréé à chaque rendu par l'appelant : le garder dans une
  // ref évite de relancer la requête à chaque frappe de clavier.
  const validerRef = useRef(valider)
  validerRef.current = valider

  useEffect(() => {
    if (chemin === null) { setEtat({ phase: 'inactive' }); return }
    let vivant = true
    setEtat({ phase: 'chargement' })
    void lireRessource(chemin, (v) => validerRef.current(v)).then((e) => { if (vivant) setEtat(e) })
    return () => { vivant = false }
  }, [chemin, tour])

  return { etat, recharger: useCallback(() => setTour((t) => t + 1), []) }
}

/**
 * Le rendu d'un état qui n'est pas `ok`. Il DIT ce qui manque, et pourquoi
 * l'écran préfère se taire.
 */
export function EtatBloc({ etat, quoi }: { etat: Etat<unknown>; quoi: string }): ReactNode {
  if (etat.phase === 'ok' || etat.phase === 'inactive') return null
  if (etat.phase === 'chargement') return <p className="lc-hint">Lecture de {quoi} en cours.</p>
  if (etat.phase === 'refus') {
    return (
      <div className="fr-state">
        <span className="fr-state__title"><Lock size={16} aria-hidden="true" /> Lecture non autorisée</span>
        <p className="fr-state__text">
          Vos droits ne couvrent pas {quoi}. Aucun montant n’a été transmis à ce
          navigateur : ce n’est pas un affichage masqué, c’est une donnée qui
          n’est jamais partie du serveur.
        </p>
      </div>
    )
  }
  if (etat.phase === 'absente') {
    return (
      <div className="fr-state">
        <span className="fr-state__title"><Radio size={16} aria-hidden="true" /> {capitaliser(quoi)} n’est pas disponible</span>
        <p className="fr-state__text">{etat.raison} Gestion préfère ne rien afficher plutôt qu’un total qui vaudrait zéro par accident.</p>
      </div>
    )
  }
  return (
    <div className="fr-state fr-state--warn">
      <span className="fr-state__title"><AlertTriangle size={16} aria-hidden="true" /> Lecture impossible</span>
      <p className="fr-state__text">{etat.raison}</p>
    </div>
  )
}

/* ══════════════════════════ lecteurs élémentaires ═══════════════════════ */

export function texte(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null
}

/** Un montant en centimes. Le pilote PostgreSQL rend un `bigint` en CHAÎNE. */
export function cents(v: unknown): number | null {
  if (v === null || v === undefined) return null
  const n = typeof v === 'string' ? Number(v) : v
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

export function entier(v: unknown): number | null {
  const n = cents(v)
  return n === null ? null : Math.trunc(n)
}

/* ══════════════════════════ dates de nuit commerciale ═══════════════════ */

/**
 * Toutes ces fonctions travaillent sur des `YYYY-MM-DD` de NUIT COMMERCIALE,
 * jamais sur des instants : la nuit du 31 août qui déborde sur le 1ᵉʳ septembre
 * est UNE nuit, portant la date annoncée. On ancre donc à midi UTC — jamais
 * `new Date(ymd)`, qui décale d'un jour selon le fuseau du navigateur.
 */
function ancre(ymd: string): Date { return new Date(`${ymd}T12:00:00Z`) }

export function ymdAujourdhui(): string {
  return new Intl.DateTimeFormat('fr-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())
}

export function ajouterJours(ymd: string, n: number): string {
  const d = ancre(ymd)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** Le lundi de la semaine contenant cette nuit. */
export function debutSemaine(ymd: string): string {
  const d = ancre(ymd)
  return ajouterJours(ymd, -((d.getUTCDay() + 6) % 7))
}

export function moisDe(ymd: string): string { return ymd.slice(0, 7) }
export function anneeDe(ym: string): number { return Number(ym.slice(0, 4)) }

export function premierJour(ym: string): string { return `${ym}-01` }
export function dernierJour(ym: string): string {
  const [a, m] = ym.split('-').map(Number) as [number, number]
  return new Date(Date.UTC(a, m, 0)).toISOString().slice(0, 10)
}
export function moisSuivant(ym: string, n: number): string {
  const [a, m] = ym.split('-').map(Number) as [number, number]
  const d = new Date(Date.UTC(a, m - 1 + n, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

const JOUR_FMT = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
const JOUR_LONG_FMT = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
const MOIS_FMT = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
const MOIS_COURT_FMT = new Intl.DateTimeFormat('fr-FR', { month: 'short', timeZone: 'UTC' })

export function capitaliser(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1) }
export function libelleJour(ymd: string): string { return capitaliser(JOUR_FMT.format(ancre(ymd))) }
export function libelleJourLong(ymd: string): string { return capitaliser(JOUR_LONG_FMT.format(ancre(ymd))) }
export function libelleMois(ym: string): string { return capitaliser(MOIS_FMT.format(ancre(`${ym}-01`))) }
export function libelleMoisCourt(ym: string): string { return capitaliser(MOIS_COURT_FMT.format(ancre(`${ym}-01`))) }

/* ══════════════════════════ composantes de recette ══════════════════════ */

/**
 * Une composante dit CE QUE C'EST (bar, billetterie, vestiaire), jamais d'où
 * le chiffre vient — c'est la distinction source / composante du modèle de
 * recette, et la confondre est le premier pas vers le double comptage.
 */
export interface Composante { code: string; libelle: string; cents: number }
export interface Colonne { code: string; libelle: string }

const LIBELLES_CONNUS: Record<string, string> = {
  BILLETTERIE: 'Billetterie',
  BILLETTERIE_ONLINE: 'Billetterie en ligne',
  BILLETTERIE_ONSITE: 'Billetterie sur place',
  BAR: 'Bar',
  VESTIAIRE: 'Vestiaire',
  OFFERTS: 'Offerts',
  FRAIS: 'Frais fournisseur',
  AUTRE: 'Autre',
}

/** Le libellé transmis prime toujours : Gestion ne renomme pas le domaine. */
export function libelleComposante(code: string, fourni?: string | null): string {
  if (fourni !== null && fourni !== undefined && fourni.trim() !== '') return fourni
  const connu = LIBELLES_CONNUS[code]
  if (connu !== undefined) return connu
  const mots = code.toLowerCase().replace(/[_-]+/g, ' ').trim()
  return mots === '' ? code : capitaliser(mots)
}

/**
 * Lit les composantes d'une charge utile. Trois formes coexistent dans l'API
 * livrée, et elles sont toutes légitimes :
 *   · `components` d'une nuit — une ligne PAR POINT DE VENTE, donc plusieurs
 *     lignes pour le même code (le bar de la ROOM 1 et celui de la ROOM 2) ;
 *   · `by_component` d'un exercice — une ligne par code, déjà agrégée ;
 *   · `by_category` d'un mois — la clé s'appelle alors `category`.
 *
 * On ne suppose donc jamais l'unicité du code : `agreger()` fait la somme
 * quand on veut une valeur par composante, et les lignes brutes restent
 * disponibles pour la ventilation par point de vente.
 */
export function composantesDe(v: unknown): Composante[] {
  const out: Composante[] = []
  if (Array.isArray(v)) {
    for (const it of v) {
      if (!estObjet(it)) continue
      const code = texte(it['component_code']) ?? texte(it['code']) ?? texte(it['category'])
      const montant = cents(it['amount_cents'] ?? it['cents'] ?? it['retained_cents'])
      if (code === null || montant === null) continue
      out.push({ code, libelle: libelleComposante(code, texte(it['label'])), cents: montant })
    }
    return agreger(out)
  }
  if (estObjet(v)) {
    for (const [code, brut] of Object.entries(v)) {
      const montant = cents(brut)
      if (montant === null) continue
      out.push({ code, libelle: libelleComposante(code), cents: montant })
    }
  }
  return out
}

/** Somme les lignes de même code, en conservant l'ordre d'apparition. */
function agreger(lignes: Composante[]): Composante[] {
  const par = new Map<string, Composante>()
  for (const c of lignes) {
    const deja = par.get(c.code)
    if (deja === undefined) par.set(c.code, { ...c })
    else deja.cents += c.cents
  }
  return [...par.values()]
}

const ORDRE_CANON = ['BILLETTERIE', 'BILLETTERIE_ONLINE', 'BILLETTERIE_ONSITE', 'BAR', 'VESTIAIRE', 'OFFERTS', 'FRAIS', 'AUTRE']
function rang(code: string): number {
  const i = ORDRE_CANON.indexOf(code)
  return i === -1 ? ORDRE_CANON.length : i
}

/**
 * LES COLONNES QUI EXISTENT VRAIMENT SUR LA PÉRIODE.
 *
 * Une colonne vide sur douze mois n'est pas une information, c'est du bruit :
 * seules apparaissent les composantes dont au moins une ligne porte un
 * montant. Elles reviennent d'elles-mêmes dès qu'un chiffre existe.
 */
export function colonnesActives(lignes: Array<{ composantes: Composante[] }>): Colonne[] {
  const vues = new Map<string, string>()
  for (const l of lignes) for (const c of l.composantes) if (!vues.has(c.code)) vues.set(c.code, c.libelle)
  return [...vues.entries()]
    .map(([code, libelle]) => ({ code, libelle }))
    .sort((a, b) => rang(a.code) - rang(b.code) || a.libelle.localeCompare(b.libelle, 'fr'))
}

/** Le montant d'une composante, ou `null` si elle n'existe pas sur la ligne. */
export function montantDe(composantes: Composante[], code: string): number | null {
  const c = composantes.find((x) => x.code === code)
  return c === undefined ? null : c.cents
}

/** Somme d'une composante sur plusieurs lignes — `null` si aucune n'en porte. */
export function totalColonne(lignes: Array<{ composantes: Composante[] }>, code: string): number | null {
  let total: number | null = null
  for (const l of lignes) {
    const m = montantDe(l.composantes, code)
    if (m === null) continue
    total = (total ?? 0) + m
  }
  return total
}

/** Somme d'un champ facultatif — `null` tant qu'aucune ligne ne le porte. */
export function totalChamp<T>(lignes: T[], lire: (l: T) => number | null): number | null {
  let total: number | null = null
  for (const l of lignes) {
    const m = lire(l)
    if (m === null) continue
    total = (total ?? 0) + m
  }
  return total
}

/**
 * Somme STRICTE : `null` dès qu'une seule ligne est inconnue, ou s'il n'y a
 * aucune ligne. C'est la somme d'un total qui PRÉTEND être complet (« total
 * des nuits validées ») : additionner les seules lignes lisibles afficherait
 * un total juste en apparence, et faux du montant masqué.
 */
export function totalStrict<T>(lignes: T[], lire: (l: T) => number | null): number | null {
  if (lignes.length === 0) return null
  let total = 0
  for (const l of lignes) {
    const m = lire(l)
    if (m === null) return null
    total += m
  }
  return total
}

/* ══════════════════════════ nuits et mois ═══════════════════════════════ */

export type StatutCloture = 'DRAFT' | 'READY' | 'CLOSED' | 'SUPERSEDED'

export const STATUT_CLOTURE_LABELS: Record<StatutCloture, string> = {
  DRAFT: 'Brouillon',
  READY: 'Prête à finaliser',
  CLOSED: 'Finalisée',
  SUPERSEDED: 'Remplacée par une révision',
}

function statutDe(v: unknown): StatutCloture | null {
  const t = texte(v)
  return t !== null && t in STATUT_CLOTURE_LABELS ? (t as StatutCloture) : null
}

export interface PdvCA { id: string; nom: string; composantes: Composante[]; retenu: number | null }

export interface NuitCA {
  date: string
  soirees: string[]
  composantes: Composante[]
  calcule: number | null
  ajustements: number | null
  retenu: number | null
  ecart: number | null
  statut: StatutCloture | null
  closingId: string | null
  revision: number | null
  pointsDeVente: PdvCA[]
}

function libellesSoirees(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  const out: string[] = []
  for (const it of v) {
    const direct = texte(it)
    if (direct !== null) { out.push(direct); continue }
    if (!estObjet(it)) continue
    const nom = texte(it['name']) ?? texte(it['title']) ?? texte(it['label'])
    if (nom !== null) out.push(nom)
  }
  return out
}

/**
 * LA VENTILATION PAR POINT DE VENTE — DÉDUITE, jamais inventée.
 *
 * L'API ne rend pas de bloc « points de vente » : elle porte le point de vente
 * SUR CHAQUE COMPOSANTE (`point_of_sale_id` / `point_of_sale_name`). C'est plus
 * juste ainsi — une composante peut n'être rattachée à aucun comptoir — mais
 * cela veut dire que le regroupement se fait ici, et qu'une composante sans
 * point de vente n'est comptée dans AUCUN d'eux : elle reste au niveau de la
 * nuit. Répartir au prorata aurait fabriqué des chiffres que personne n'a
 * constatés.
 */
function pointsDeVenteDe(composantes: unknown): PdvCA[] {
  if (!Array.isArray(composantes)) return []
  const par = new Map<string, PdvCA>()
  for (const it of composantes) {
    if (!estObjet(it)) continue
    const nom = texte(it['point_of_sale_name'])
    const id = texte(it['point_of_sale_id']) ?? nom
    const code = texte(it['component_code']) ?? texte(it['code'])
    const montant = cents(it['amount_cents'])
    if (id === null || nom === null || code === null || montant === null) continue
    const deja = par.get(id) ?? { id, nom, composantes: [], retenu: 0 }
    const ligne = deja.composantes.find((c) => c.code === code)
    if (ligne === undefined) deja.composantes.push({ code, libelle: libelleComposante(code, texte(it['label'])), cents: montant })
    else ligne.cents += montant
    deja.retenu = (deja.retenu ?? 0) + montant
    par.set(id, deja)
  }
  return [...par.values()].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
}

/**
 * Une nuit du read model `GET /finance/revenue/nights`.
 *
 * Le total d'une nuit s'appelle `total_cents` côté API : il n'y a NI calculé NI
 * ajustements à ce niveau, parce qu'un ajustement porte sur un mois ou une
 * année, jamais sur une nuit. Ces champs restent donc `null`, et l'écran ne
 * dessine les trois lignes que là où elles existent — plutôt que d'afficher
 * « Ajustements : 0,00 € » sur une nuit qui n'en a jamais eu la possibilité.
 */
export function nuitDe(v: unknown): NuitCA | null {
  if (!estObjet(v)) return null
  const date = texte(v['business_date']) ?? texte(v['date'])
  if (date === null || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  const cloture = estObjet(v['closing']) ? v['closing'] : null
  const brutComposantes = v['components'] ?? v['by_component']
  return {
    date,
    soirees: libellesSoirees(v['soirees'] ?? v['events']),
    composantes: composantesDe(brutComposantes),
    calcule: cents(v['calculated_cents']),
    ajustements: cents(v['adjustments_cents']),
    retenu: cents(v['total_cents'] ?? v['retained_cents']),
    ecart: cents(v['discrepancy_cents']),
    statut: statutDe(cloture?.['status'] ?? v['closing_status']),
    closingId: texte(cloture?.['id']) ?? texte(v['closing_id']),
    revision: entier(cloture?.['revision'] ?? v['revision']),
    pointsDeVente: pointsDeVenteDe(brutComposantes),
  }
}

export interface MoisCA {
  mois: string
  composantes: Composante[]
  calcule: number | null
  /**
   * Le chiffre des nuits VALIDÉES SANS CLÔTURE DÉTAILLÉE du mois
   * (`validated_cents`) : il compte dans le retenu, à part du calculé. `null`
   * = non servi — l'écran écrit « — », jamais un zéro qui affirmerait qu'aucune
   * nuit n'a été validée.
   */
  valide: number | null
  ajustements: number | null
  retenu: number | null
  /** Nuits CLÔTURÉES (celles qui ont des composantes). */
  nuits: number | null
  /** Nuits validées sans clôture détaillée (`validated_nights`). */
  nuitsValidees: number | null
  cloturees: number | null
}

export function moisCADe(v: unknown, anneeDefaut: number | null): MoisCA | null {
  if (!estObjet(v)) return null
  const direct = texte(v['month'])
  let mois: string | null = direct !== null && /^\d{4}-\d{2}$/.test(direct) ? direct : null
  if (mois === null) {
    const numero = entier(v['month'])
    const annee = entier(v['year']) ?? anneeDefaut
    if (numero === null || annee === null || numero < 1 || numero > 12) return null
    mois = `${annee}-${String(numero).padStart(2, '0')}`
  }
  return {
    mois,
    // Un mois est ventilé par CATÉGORIE (`by_category`), l'exercice par
    // composante (`by_component`) : deux granularités, un seul affichage.
    composantes: composantesDe(v['by_category'] ?? v['components'] ?? v['by_component']),
    calcule: cents(v['calculated_cents']),
    valide: cents(v['validated_cents']),
    ajustements: cents(v['adjustments_cents']),
    retenu: cents(v['retained_cents']),
    nuits: entier(v['nights_count'] ?? v['nights']),
    nuitsValidees: entier(v['validated_nights']),
    cloturees: entier(v['closed_nights']),
  }
}

/** `GET /finance/revenue/nights` — la liste, quel que soit son nom de champ. */
export function lireNuits(v: unknown): NuitCA[] | null {
  const brut = estObjet(v) ? (v['nights'] ?? v['rows'] ?? v['data']) : v
  if (!Array.isArray(brut)) return null
  return brut.map(nuitDe).filter((n): n is NuitCA => n !== null)
}

/**
 * UNE NUIT VALIDÉE SANS CLÔTURE DÉTAILLÉE — un chiffre arrêté EN BLOC par
 * l'exploitant (une nuit d'avant Gestion, ou dont la clôture a été ignorée).
 *
 * Elle n'a ni composantes, ni point de vente, ni clôture à ouvrir : c'est
 * pour cela qu'elle a son propre type, et jamais une place dans `NuitCA`. La
 * fondre parmi les nuits clôturées lui prêterait une révision, une ventilation
 * et un « calculé » qu'elle n'a pas — et le total des clôtures mentirait.
 */
export interface NuitValideeCA {
  date: string
  /** `null` = montant non servi : « — », jamais 0. */
  cents: number | null
  entrees: number | null
  auteur: string | null
  /** Horodatage ISO de la validation. */
  quand: string | null
  motif: string | null
  /** Nuit reprise de l'historique (`reprise-historique.ts`) ; `null` = validée à l'écran. */
  reprise: RepriseHistorique | null
}

export function nuitValideeDe(v: unknown): NuitValideeCA | null {
  if (!estObjet(v)) return null
  const date = texte(v['business_date'])
  if (date === null || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  return {
    date,
    cents: cents(v['revenue_cents']),
    entrees: entier(v['entries']),
    auteur: texte(v['validated_by_name']),
    quand: texte(v['validated_at']),
    motif: texte(v['reason']),
    reprise: repriseDe(v['history']),
  }
}

/**
 * Une PÉRIODE de nuits telle que `GET /finance/revenue/nights` la sert : les
 * nuits CLÔTURÉES d'un côté, les nuits VALIDÉES SANS CLÔTURE de l'autre — le
 * serveur les range déjà séparément (`nights` / `validated_nights`), et
 * l'écran garde ce rangement jusqu'au bout.
 */
export interface PeriodeNuitsCA {
  nuits: NuitCA[]
  validees: NuitValideeCA[]
}

/**
 * `GET /finance/revenue/nights?from&to` — les deux familles de nuits.
 *
 * Un serveur qui ne servirait pas encore `validated_nights` rend une liste
 * VIDE de nuits validées, pas une erreur : les nuits clôturées restent vraies
 * à elles seules, et l'écran ne prétend rien sur ce qu'il n'a pas reçu.
 */
export function lirePeriodeNuits(v: unknown): PeriodeNuitsCA | null {
  const nuits = lireNuits(v)
  if (nuits === null) return null
  const brut = estObjet(v) ? v['validated_nights'] : null
  const validees = Array.isArray(brut)
    ? brut.map(nuitValideeDe).filter((n): n is NuitValideeCA => n !== null)
    : []
  return { nuits, validees }
}

/** `GET /finance/revenue/months?year=…` */
export function lireMois(v: unknown): MoisCA[] | null {
  const annee = estObjet(v) ? entier(v['year']) : null
  const brut = estObjet(v) ? (v['months'] ?? v['rows'] ?? v['data']) : v
  if (!Array.isArray(brut)) return null
  return brut.map((m) => moisCADe(m, annee)).filter((m): m is MoisCA => m !== null)
}

/**
 * `GET /finance/revenue/annual?year=…` — l'exercice, ses mois, et l'année
 * précédente QUAND elle existe.
 *
 * Deux absences à ne jamais confondre : `saison: null` signifie que
 * l'exercice comptable n'est PAS CONFIGURÉ (les écrans disent alors « Saison à
 * configurer » et n'inventent aucune date de début) ; `precedent: null`
 * signifie qu'il n'y a pas d'historique comparable, et la comparaison
 * disparaît au lieu d'afficher « −100 % » contre un zéro fabriqué.
 */
export interface TotauxCA {
  calcule: number | null
  /** Le chiffre des nuits validées sans clôture détaillée de la période. */
  valide: number | null
  ajustements: number | null
  retenu: number | null
  composantes: Composante[]
}

/** Ce que la comparaison annuelle a besoin de savoir d'un mois de référence. */
export interface RefMois { mois: string; retenu: number | null }

export interface AnnuelCA {
  annee: number | null
  /** Nuits CLÔTURÉES de l'année civile. */
  nuits: number | null
  /** Nuits validées sans clôture détaillée de l'année civile. */
  nuitsValidees: number | null
  /** Totaux de l'ANNÉE CIVILE consultée. */
  totaux: TotauxCA
  mois: MoisCA[]
  /** L'EXERCICE ouvert pendant cette année — `null` = non configuré. */
  saison: { debut: string; fin: string; nuits: number | null; nuitsValidees: number | null; totaux: TotauxCA } | null
  comparaison: {
    anneePrecedente: number | null
    actuel: number | null
    precedent: number | null
    mois: RefMois[]
    /** Ce qui LIMITE la comparaison, en français. Un retard de saisie n'est pas un effondrement. */
    reserves: string[]
  } | null
  ajustements: AjustementCA[]
}

export interface AjustementCA {
  id: string
  portee: string
  debut: string | null
  fin: string | null
  composante: string | null
  cents: number | null
  motif: string
  auteur: string | null
  quand: string | null
}

function totauxDe(v: unknown): TotauxCA {
  const o = estObjet(v) ? v : {}
  return {
    calcule: cents(o['calculated_cents']),
    valide: cents(o['validated_cents']),
    ajustements: cents(o['adjustments_cents']),
    retenu: cents(o['retained_cents']),
    composantes: composantesDe(o['components'] ?? o['by_component']),
  }
}

function saisonDe(v: unknown): AnnuelCA['saison'] {
  if (!estObjet(v)) return null
  const debut = texte(v['start'])
  const fin = texte(v['end'])
  if (debut === null || fin === null) return null
  return {
    debut, fin, nuits: entier(v['nights']), nuitsValidees: entier(v['validated_nights']), totaux: totauxDe(v),
  }
}

function ajustementDe(v: unknown): AjustementCA | null {
  if (!estObjet(v)) return null
  const id = texte(v['id'])
  const motif = texte(v['reason'])
  if (id === null || motif === null) return null
  return {
    id,
    portee: texte(v['scope']) ?? 'MOIS',
    debut: texte(v['period_start']),
    fin: texte(v['period_end']),
    composante: texte(v['component_code']),
    cents: cents(v['amount_cents']),
    motif,
    auteur: texte(v['created_by_name']),
    quand: texte(v['created_at']),
  }
}

export function lireAnnuel(v: unknown): AnnuelCA | null {
  if (!estObjet(v)) return null
  const mois = lireMois(v)
  if (mois === null) return null
  const comp = estObjet(v['comparison']) ? v['comparison'] : null
  const precedentCents = comp === null ? null : cents(comp['previous_cents'])
  const moisComp = comp !== null && Array.isArray(comp['months'])
    ? comp['months'].flatMap((m): RefMois[] => {
      if (!estObjet(m)) return []
      const ym = texte(m['month'])
      return ym === null ? [] : [{ mois: ym, retenu: cents(m['previous_cents']) }]
    })
    : []
  // Le serveur rend déjà `comparison: null` quand la comparaison n'a pas de
  // sens. On refuse en plus de comparer à une année dont le total est absent :
  // « −100 % » contre un vide a l'air d'une information et n'en est pas une.
  return {
    annee: entier(v['year']),
    nuits: entier(v['nights']),
    nuitsValidees: entier(v['validated_nights']),
    totaux: totauxDe(v['totals'] ?? v),
    mois,
    saison: saisonDe(v['season']),
    comparaison: comp !== null && precedentCents !== null
      ? {
        anneePrecedente: entier(comp['previous_year']),
        actuel: cents(comp['current_cents']),
        precedent: precedentCents,
        mois: moisComp,
        reserves: Array.isArray(comp['caveats'])
          ? comp['caveats'].flatMap((c) => { const t = texte(c); return t === null ? [] : [t] })
          : [],
      }
      : null,
    ajustements: Array.isArray(v['adjustments'])
      ? v['adjustments'].map(ajustementDe).filter((a): a is AjustementCA => a !== null)
      : [],
  }
}

/**
 * `GET /finance/revenue/season?date=…` — l'exercice qui CONTIENT la date, et
 * tous ses mois.
 *
 * `saison: null` garde le sens qu'elle a dans `lireAnnuel` : l'exercice n'est
 * pas configuré. Une réponse illisible rend `null` tout court, et l'écran dit
 * « non lu » au lieu de « à configurer ».
 */
export interface SaisonCA {
  saison: NonNullable<AnnuelCA['saison']> | null
  /** Les mois civils de l'exercice, dans l'ordre, seuls ceux qui ont une donnée. */
  mois: MoisCA[]
}

export function lireSaison(v: unknown): SaisonCA | null {
  if (!estObjet(v) || !('season' in v)) return null
  if (v['season'] === null) return { saison: null, mois: [] }
  const saison = saisonDe(v['season'])
  if (saison === null) return null
  const mois = lireMois(v['season'])
  return { saison, mois: (mois ?? []).slice().sort((x, y) => x.mois.localeCompare(y.mois)) }
}

/**
 * Le même jour, `sens` exercices plus loin. Un exercice dure un an, donc
 * décaler la date d'un an tombe dans l'exercice voisin quel que soit le jour
 * d'ouverture — à condition de GARDER le jour : l'ancien pas ramenait au 15
 * du mois, et le 25/09 d'un exercice ouvert le 21/09 retombait au 15/09… dans
 * l'exercice d'avant, soit un pas de zéro. Le 29 février devient le 28.
 */
export function exerciceVoisin(jour: string, sens: 1 | -1): string {
  const an = Number(jour.slice(0, 4)) + sens
  const mois = Number(jour.slice(5, 7))
  const max = new Date(Date.UTC(an, mois, 0)).getUTCDate()
  const j = Math.min(Number(jour.slice(8, 10)), max)
  return `${an}-${jour.slice(5, 7)}-${String(j).padStart(2, '0')}`
}

/** L'évolution en pourcentage — `null` si la référence est absente ou nulle. */
export function evolution(actuel: number | null, reference: number | null): number | null {
  if (actuel === null || reference === null || reference === 0) return null
  return ((actuel - reference) / Math.abs(reference)) * 100
}

/* ══════════════════════════ affichages partagés ═════════════════════════ */

function accord(n: number, mot: string): string { return n > 1 ? `${mot}s` : mot }
function pluriel(n: number, mot: string): string { return `${n} ${accord(n, mot)}` }

/**
 * LES NUITS D'UNE PÉRIODE, DITES EN DEUX FAMILLES.
 *
 * « 12 nuits clôturées · 3 validées sans clôture ». Un mois peut n'avoir QUE
 * des nuits validées : il s'écrit alors « 3 nuits validées sans clôture », et
 * non « 0 nuit » — qui ferait croire à un mois vide alors qu'il a un chiffre
 * arrêté. `null` quand rien n'est servi : l'appelant écrit « — ».
 */
export function nuitsTexte(cloturees: number | null, validees: number | null): string | null {
  const v = validees !== null && validees > 0 ? validees : null
  const c = cloturees !== null && (cloturees > 0 || v === null) ? cloturees : null
  if (c !== null && v !== null) return `${pluriel(c, 'nuit')} ${accord(c, 'clôturée')} · ${pluriel(v, 'validée')} sans clôture`
  if (c !== null) return `${pluriel(c, 'nuit')} ${accord(c, 'clôturée')}`
  if (v !== null) return `${pluriel(v, 'nuit')} ${accord(v, 'validée')} sans clôture`
  return null
}

/** Y a-t-il, sur ces lignes, au moins une nuit validée sans clôture détaillée ? */
export function aDesValidees(lignes: Array<{ nuitsValidees: number | null; valide: number | null }>): boolean {
  return lignes.some((l) => (l.nuitsValidees ?? 0) > 0 || (l.valide !== null && l.valide !== 0))
}

/** Le libellé canonique de la ligne — le même partout, à la lettre près. */
export const LIBELLE_VALIDE = 'Validé sans clôture détaillée'

/**
 * LES LIGNES DU RETENU : Calculé · Validé sans clôture détaillée ·
 * Ajustements · Retenu.
 *
 * Un total retenu qui diffère du calcul n'est jamais une correction
 * silencieuse : l'écart porte un ajustement motivé, et l'écran montre chaque
 * ligne plutôt que le seul résultat. Les nuits validées sans clôture
 * détaillée comptent dans le retenu, NOMMÉES à part — jamais fondues dans le
 * calculé, qui reste la somme des composantes des clôtures finalisées. Le
 * lecteur doit pouvoir refaire l'addition à l'écran :
 *
 *     Calculé + Validé sans clôture détaillée + Ajustements = Retenu
 *
 * `valide` ABSENT (`undefined`) = la notion ne s'applique pas à ce niveau (une
 * nuit clôturée n'a rien de « validé en bloc ») : la ligne n'est pas dessinée.
 * `valide: null` = le serveur ne l'a pas servi : « — », jamais « 0,00 € ».
 */
export function TroisLignes(props: {
  calcule: Cents
  valide?: Cents
  /** Combien de nuits portent ce chiffre validé — dit à côté du libellé. */
  nuitsValidees?: number | null
  ajustements: Cents
  retenu: Cents
  note?: ReactNode
}) {
  const nv = props.nuitsValidees ?? null
  return (
    <div className="fr-triline">
      <span className="fr-triline__label">Calculé</span>
      <span className="fi-money fi-money--faible">{formatMoney(props.calcule)}</span>
      {props.valide !== undefined ? (
        <>
          <span className="fr-triline__label">
            {LIBELLE_VALIDE}
            {nv !== null && nv > 0 ? <span className="fr-triline__compte"> · {pluriel(nv, 'nuit')}</span> : null}
          </span>
          <span className="fi-money">{formatMoney(props.valide)}</span>
        </>
      ) : null}
      <span className="fr-triline__label">Ajustements</span>
      <span className="fi-money">{formatMoney(props.ajustements, { signe: true })}</span>
      <span className="fr-triline__sep" />
      <span className="fr-triline__label fr-triline__label--fort">Retenu</span>
      <span className="fi-money fi-money--fort">{formatMoney(props.retenu)}</span>
      {props.note !== undefined ? <span className="fr-triline__note">{props.note}</span> : null}
    </div>
  )
}

/** L'écart, signe écrit et couleur secondaire — jamais la couleur seule. */
export function Ecart({ cents: c }: { cents: Cents }) {
  const e = formatEcart(c)
  return <span className={`fi-ecart fi-ecart--${e.ton}`}>{e.texte}</span>
}

/**
 * LA COMPARAISON À L'ANNÉE PRÉCÉDENTE — et son absence assumée.
 *
 * Elle ne s'affiche QUE si la référence existe réellement. Comparer à une
 * absence produirait « −100 % », qui a l'air d'une information et n'en est
 * pas : un zéro faux est pire qu'une case vide.
 */
export function Comparaison({ actuel, reference, quoi }: {
  actuel: number | null; reference: number | null; quoi?: string
}) {
  const pct = evolution(actuel, reference)
  if (pct === null) return null
  const ton = Math.abs(pct) < 0.5 ? 'flat' : pct > 0 ? 'up' : 'down'
  const signe = pct > 0 ? '+' : ''
  const suffixe = quoi !== undefined && quoi !== '' ? ` ${quoi}` : ''
  return (
    <span className={`fr-compare fr-compare--${ton}`} title={`Référence : ${formatMoney(reference)}`}>
      {signe}{pct.toFixed(1).replace('.', ',')} %{suffixe} ({formatMoney(reference)})
    </span>
  )
}

/**
 * LA PASTILLE D'UNE NUIT VALIDÉE SANS CLÔTURE DÉTAILLÉE — même icône que la
 * case du calendrier des nuits (`BadgeCheck`), pour qu'une nuit se reconnaisse
 * d'un écran à l'autre. Elle ne dit jamais « Finalisée » : il n'y a pas de
 * clôture derrière.
 */
export function PastilleValidee(props: { reprise?: RepriseHistorique | null } = {}) {
  // Une nuit reprise de l'historique n'a été validée par personne à l'écran :
  // sa pastille dit d'où vient le chiffre (la saison est dans l'infobulle).
  if (props.reprise) {
    return (
      <span className="lc-pill lc-pill--ok" title={props.reprise.label}>
        <History size={14} aria-hidden="true" /> {REPRIS_COURT}
      </span>
    )
  }
  return (
    <span className="lc-pill lc-pill--ok">
      <BadgeCheck size={14} aria-hidden="true" /> Validée sans clôture
    </span>
  )
}

const VALIDATION_FMT = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris' })

/**
 * « Validée le 7 octobre 2026 par Hugo » — ce qui est connu, rien d'autre. Une
 * nuit reprise de l'historique dit sa source et où la relire, jamais « par ».
 */
export function libelleValidation(v: Pick<NuitValideeCA, 'auteur' | 'quand'> & { reprise?: RepriseHistorique | null }): string {
  if (v.reprise) return phraseReprise(v.reprise)
  const d = v.quand === null ? null : new Date(v.quand)
  const quand = d !== null && !Number.isNaN(d.getTime()) ? `le ${VALIDATION_FMT.format(d)}` : null
  return ['Validée', quand, v.auteur !== null ? `par ${v.auteur}` : null].filter(Boolean).join(' ')
}

export function PastilleCloture({ statut }: { statut: StatutCloture | null }) {
  if (statut === null) return <span className="lc-pill lc-pill--idle">Pas de clôture</span>
  const ton = statut === 'CLOSED' ? ' lc-pill--ok' : statut === 'READY' ? ' lc-pill--warning' : ' lc-pill--idle'
  return <span className={`lc-pill${ton}`}>{STATUT_CLOTURE_LABELS[statut]}</span>
}

/* ══════════════════════════ motif de correction ═════════════════════════ */

/** Douze caractères UTILES — la base impose la même chose, côté serveur. */
export const MOTIF_MINI = 12
export function motifValide(v: string): boolean { return v.trim().length >= MOTIF_MINI }

export function MotifChamp(props: {
  valeur: string
  onChange: (v: string) => void
  label: string
  aide?: string
  autoFocus?: boolean
}) {
  const reste = MOTIF_MINI - props.valeur.trim().length
  return (
    <div className="fr-motif">
      <span className="lc-label">{props.label}</span>
      <textarea
        className="lc-input"
        value={props.valeur}
        maxLength={500}
        autoFocus={props.autoFocus === true}
        aria-label={props.label}
        onChange={(e) => props.onChange(e.target.value)}
      />
      <span className={`fr-motif__count${reste > 0 ? ' fr-motif__count--court' : ''}`}>
        {reste > 0
          ? `Encore ${reste} caractère${reste > 1 ? 's' : ''} : un motif court ne dit rien à qui relira dans six mois.`
          : (props.aide ?? 'Ce motif reste attaché à la correction, avec votre nom et la date.')}
      </span>
    </div>
  )
}
